import { useToast } from "@/hooks/use-toast";
import { useEffect, useState } from "react";
import { useQueryClient, useMutation, useQuery } from "@tanstack/react-query";
import { apiRequest, parseErrorMessage } from "@/lib/queryClient";
import { Card } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Crown, Loader2 } from "lucide-react";
import { FoundingAccessSettingsResponse } from "./types";
import { toLocalInputValue } from "./helpers";
import { FoundingAccessHistoryList } from "./FoundingAccessHistoryList";

export function FoundingAccessDatePanel() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [launchDateInput, setLaunchDateInput] = useState("");
  const [initialized, setInitialized] = useState(false);

  const settingsQuery = useQuery<FoundingAccessSettingsResponse>({
    queryKey: ["/api/admin/settings/founding-access-date"],
    queryFn: async () => {
      const res = await fetch("/api/admin/settings/founding-access-date", { credentials: "include" });
      if (!res.ok) throw new Error("Failed to load Founder Circle launch date");
      return res.json();
    },
  });

  useEffect(() => {
    if (settingsQuery.data && !initialized) {
      setLaunchDateInput(toLocalInputValue(settingsQuery.data.settings?.launchDate ?? null));
      setInitialized(true);
    }
  }, [settingsQuery.data, initialized]);

  const saveMutation = useMutation({
    mutationFn: async (val: string) => {
      const launchDate = val ? new Date(val) : null;
      if (val && (!launchDate || Number.isNaN(launchDate.getTime()))) {
        throw new Error("Please enter a valid date and time.");
      }
      return apiRequest("PUT", "/api/admin/settings/founding-access-date", {
        launchDate: launchDate ? launchDate.toISOString() : null,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/settings/founding-access-date"] });
      queryClient.invalidateQueries({ queryKey: ["/api/founding-access-date"] });
      toast({
        title: "Founder Circle launch date updated",
        description: "Landing page countdown and copy will pick up the new date.",
      });
    },
    onError: (err: any) =>
      toast({ title: "Failed to save", description: parseErrorMessage(err, "Couldn't save. Please try again."), variant: "destructive" }),
  });

  const fallback = settingsQuery.data?.fallback;
  const currentDb = settingsQuery.data?.settings?.launchDate ?? null;
  let previewIso: string | null = null;
  if (launchDateInput) {
    const parsed = new Date(launchDateInput);
    previewIso = Number.isNaN(parsed.getTime()) ? null : parsed.toISOString();
  } else {
    previewIso = currentDb ?? fallback ?? null;
  }
  const previewLabel = previewIso ? new Date(previewIso).toLocaleString() : "—";
  const isUsingFallback = !launchDateInput && !currentDb;

  return (
    <div className="max-w-2xl space-y-4">
      <Card className="p-5 space-y-5">
        <div>
          <h3 className="font-semibold text-sm mb-1" data-testid="text-founding-access-date-title">
            Founder Circle Launch Date
          </h3>
          <p className="text-xs text-muted-foreground">
            Controls the countdown timer and "Founding Access" copy shown on the public landing page.
            Saved values override the <code className="text-[11px]">VITE_FOUNDING_ACCESS_DATE</code>{" "}
            environment variable; clear the field below to fall back to the env var / compiled default.
            Changes take effect immediately — no redeploy required.
          </p>
        </div>

        {settingsQuery.isLoading ? (
          <div className="space-y-3">{[1, 2].map((i) => <Skeleton key={i} className="h-9 w-full" />)}</div>
        ) : (
          <div className="space-y-4">
            <div>
              <Label className="text-xs">Launch date &amp; time</Label>
              <div className="flex gap-2 mt-1">
                <Input
                  type="datetime-local"
                  className="h-9 text-sm"
                  value={launchDateInput}
                  onChange={(e) => setLaunchDateInput(e.target.value)}
                  data-testid="input-founding-access-date"
                  data-tour="admin-founding-access-date-input"
                />
                {launchDateInput && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="h-9 px-2 text-xs"
                    onClick={() => setLaunchDateInput("")}
                    data-testid="button-clear-founding-access-date"
                  >
                    Clear
                  </Button>
                )}
              </div>
              <p className="text-[11px] text-muted-foreground mt-1">
                Uses your browser's local time zone. Leave blank to use the env-var / compiled default.
              </p>
            </div>

            <div className="rounded-md border bg-muted/40 px-3 py-2.5 text-xs space-y-1" data-testid="preview-founding-access-date">
              <p className="font-medium text-foreground">Preview</p>
              <p>
                <span className="text-muted-foreground">Effective date:</span>{" "}
                <span data-testid="preview-founding-access-date-value">{previewLabel}</span>
              </p>
              <p>
                <span className="text-muted-foreground">Source:</span>{" "}
                <span data-testid="preview-founding-access-date-source">
                  {isUsingFallback ? "Env var / compiled default (no admin override)" : "Admin override"}
                </span>
              </p>
              {fallback && (
                <p className="text-muted-foreground">
                  Fallback: {new Date(fallback).toLocaleString()}
                </p>
              )}
            </div>

            <Button
              className="w-full"
              onClick={() => saveMutation.mutate(launchDateInput)}
              disabled={saveMutation.isPending}
              data-testid="button-save-founding-access-date"
              data-tour="admin-founding-access-date-save"
            >
              {saveMutation.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              <Crown className="h-4 w-4 mr-2" /> Save Launch Date
            </Button>

            <FoundingAccessHistoryList history={settingsQuery.data?.history ?? []} />
          </div>
        )}
      </Card>
    </div>
  );
}
